import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import config, { isDefaultCluster } from '../Config';
import { authFetch } from '../functions/authFetch';
import { HandleResponse } from '../functions/HandleResponse';
import './pages.css';

const JOB_TYPES = [
  { id: 'mcr',       label: 'MCR',       desc: 'Mermadic Container Registry — no credentials needed' },
  { id: 'dockerhub', label: 'DockerHub',  desc: 'Public or private Docker Hub image' },
  { id: 'ecr',       label: 'ECR',        desc: 'AWS Elastic Container Registry' },
  { id: 'acr',       label: 'ACR',        desc: 'Azure Container Registry' },
];

const ENDPOINTS = {
  mcr:       '/v1/mcr/create-job',
  dockerhub: '/v1/dockerhub/create-job',
  ecr:       '/v1/ecr/create-job',
  acr:       '/v1/acr/create-job',
};

const emptyKV = () => ({ key: '', value: '' });

export default function CreateJob() {
  const navigate = useNavigate();
  const base = config.RUNNER_API;

  const [jobType, setJobType] = useState('mcr');

  const [image, setImage] = useState('');

  // command args as key-value pairs
  const [useCommandArgs, setUseCommandArgs] = useState(false);
  const [cmdArgs, setCmdArgs] = useState([emptyKV()]);

  // resources
  const [useResources, setUseResources] = useState(false);
  const [cpu, setCpu] = useState('0.5');
  const [memory, setMemory] = useState('0.25');

  // GPU — Premium clusters only, so the toggle stays hidden until the tier is known
  // to be 'prod'. Never sent otherwise; the API rejects it on other tiers anyway.
  const [clusterTier, setClusterTier] = useState(null);
  const [useGpu, setUseGpu] = useState(false);
  const gpuAvailable = clusterTier === 'prod';

  // env vars
  const [useEnv, setUseEnv] = useState(false);
  const [envVars, setEnvVars] = useState([emptyKV()]);

  // dockerhub optional credential
  const [useDockerCred, setUseDockerCred] = useState(false);
  const [dockerUser, setDockerUser] = useState('');
  const [dockerPass, setDockerPass] = useState('');

  // ecr credential (required)
  const [awsKey, setAwsKey] = useState('');
  const [awsSecret, setAwsSecret] = useState('');
  const [awsRegion, setAwsRegion] = useState('us-east-1');

  // acr credential (required)
  const [azureUser, setAzureUser] = useState('');
  const [azurePass, setAzurePass] = useState('');

  // mcr image list
  const [mcrImages, setMcrImages] = useState([]);
  const [mcrLoading, setMcrLoading] = useState(false);

  // configmap — available on every tier. On shared clusters ConfigMaps are stored
  // as env files in S3 rather than as Kubernetes ConfigMaps, but the UX is identical.
  const isShared = isDefaultCluster();
  const [useConfigMap, setUseConfigMap] = useState(false);
  const [configMapName, setConfigMapName] = useState('');
  const [configMaps, setConfigMaps] = useState([]);
  const [configMapsLoading, setConfigMapsLoading] = useState(false);

  // secret — same injection point as a ConfigMap, but the values are write-only
  // and are read straight from encrypted storage when the job starts.
  const [useSecret, setUseSecret] = useState(false);
  const [secretName, setSecretName] = useState('');
  const [secrets, setSecrets] = useState([]);
  const [secretsLoading, setSecretsLoading] = useState(false);

  // submit
  const [submitting, setSubmitting] = useState(false);
  const [resultMsg, setResultMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const fetchMcrImages = useCallback(async () => {
    setMcrLoading(true);
    try {
      const res = await authFetch(`${base}/v1/cdci/mcr/images?image_type=short`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setMcrImages(Array.isArray(data) ? data.map(i => i.image_name || i) : []);
      }
    } catch { /* ignore */ }
    finally { setMcrLoading(false); }
  }, [base]);

  const fetchConfigMaps = useCallback(async () => {
    setConfigMapsLoading(true);
    try {
      const res = await authFetch(`${base}/v1/deployment/list-configmap`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setConfigMaps(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ }
    finally { setConfigMapsLoading(false); }
  }, [base]);

  const fetchSecrets = useCallback(async () => {
    setSecretsLoading(true);
    try {
      const res = await authFetch(`${base}/v1/secret/list`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setSecrets(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ }
    finally { setSecretsLoading(false); }
  }, [base]);

  useEffect(() => {
    if (jobType === 'mcr') fetchMcrImages();
  }, [jobType, fetchMcrImages]);

  useEffect(() => {
    authFetch(`${base}/v1/deployment/get-cluster-tier`, { method: 'GET' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.tier) setClusterTier(d.tier); })
      .catch(() => {});
  }, [base]);

  useEffect(() => {
    if (useConfigMap && configMaps.length === 0) fetchConfigMaps();
  }, [useConfigMap, fetchConfigMaps, configMaps.length]);

  useEffect(() => {
    if (useSecret && secrets.length === 0) fetchSecrets();
  }, [useSecret, fetchSecrets, secrets.length]);

  // generic key-value helpers
  const updateRow = (setter) => (idx, field, val) =>
    setter(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  const addRow = (setter) => () => setter(prev => [...prev, emptyKV()]);
  const removeRow = (setter) => (idx) => setter(prev => prev.filter((_, i) => i !== idx));

  const updateCmd = updateRow(setCmdArgs);
  const addCmd = addRow(setCmdArgs);
  const removeCmd = removeRow(setCmdArgs);

  const updateEnv = updateRow(setEnvVars);
  const addEnv = addRow(setEnvVars);
  const removeEnv = removeRow(setEnvVars);

  const buildPayload = () => {
    const body = { image };

    if (useCommandArgs) {
      const args = [];
      for (const row of cmdArgs) {
        const k = row.key.trim();
        const v = row.value.trim();
        if (!k) continue;
        const flag = k.replace(/^-+/, '');
        args.push(`--${flag}`);
        if (v) args.push(v);
      }
      if (args.length) body.commandArgs = args;
    }

    if (useResources) {
      body.resourceRequests = {
        cpu: parseFloat(cpu) || 0.5,
        memory: parseFloat(memory) || 0.25,
      };
    }

    if (gpuAvailable && useGpu) {
      body.useGpu = true;
    }

    if (useEnv) {
      const pairs = envVars.filter(r => r.key.trim()).map(r => ({ name: r.key.trim(), value: r.value }));
      if (pairs.length) body.env = pairs;
    }

    if (useConfigMap && configMapName.trim()) {
      body.configmapName = configMapName.trim();
    }

    if (useSecret && secretName.trim()) {
      body.secretName = secretName.trim();
    }

    if (jobType === 'dockerhub' && useDockerCred && dockerUser.trim() && dockerPass.trim()) {
      body.credential = {
        dockerhubUsername: dockerUser.trim(),
        dockerhubPassword: dockerPass.trim(),
      };
    }

    if (jobType === 'ecr') {
      body.credential = {
        awsAccessKeyId: awsKey.trim(),
        awsSecretAccessKey: awsSecret.trim(),
        regionName: awsRegion.trim() || 'us-east-1',
      };
    }

    if (jobType === 'acr') {
      body.credential = {
        azureUsername: azureUser.trim(),
        azurePassword: azurePass.trim(),
      };
    }

    return body;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResultMsg(null);
    setErrorMsg(null);

    if (!image.trim()) { setErrorMsg('Image is required.'); return; }
    if (jobType === 'ecr' && (!awsKey.trim() || !awsSecret.trim())) {
      setErrorMsg('AWS credentials are required for ECR jobs.'); return;
    }
    if (jobType === 'acr' && (!azureUser.trim() || !azurePass.trim())) {
      setErrorMsg('Azure credentials are required for ACR jobs.'); return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${base}${ENDPOINTS[jobType]}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const jobId = await res.text().catch(() => '');
        setResultMsg(`Job created successfully.${jobId ? ` Job ID: ${jobId.replace(/"/g, '')}` : ''}`);
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.detail || `Job creation failed (${res.status}).`);
      }
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  /** Render a key-value pair list (used for both command args and env vars) */
  const renderKVList = (rows, enabled, updateFn, addFn, removeFn, keyPlaceholder, valuePlaceholder) => (
    <div className={`cj-kv-list ${!enabled ? 'cj-disabled' : ''}`}>
      {rows.map((row, i) => (
        <div key={i} className="cj-kv-row">
          <input
            className="cj-input cj-kv-key"
            placeholder={keyPlaceholder}
            value={row.key}
            onChange={e => updateFn(i, 'key', e.target.value)}
            disabled={!enabled}
            autoComplete="off"
          />
          <input
            className="cj-input cj-kv-val"
            placeholder={valuePlaceholder}
            value={row.value}
            onChange={e => updateFn(i, 'value', e.target.value)}
            disabled={!enabled}
            autoComplete="off"
          />
          {enabled && (
            <button type="button" className="cj-kv-remove" onClick={() => removeFn(i)} title="Remove">×</button>
          )}
        </div>
      ))}
      {enabled && (
        <button type="button" className="cj-kv-add" onClick={addFn}>+ Add</button>
      )}
    </div>
  );

  return (
    <div className="cj-page">
      <h2 className="cj-title">Create Job</h2>
      <p className="cj-subtitle">Run a container image as a Kubernetes job.</p>

      {/* ── Job type selector ── */}
      <div className="cj-type-bar">
        {JOB_TYPES.map(t => (
          <button
            key={t.id}
            type="button"
            className={`cj-type-btn ${jobType === t.id ? 'active' : ''}`}
            onClick={() => { setJobType(t.id); setImage(''); setErrorMsg(null); setResultMsg(null); }}
          >
            <span className="cj-type-label">{t.label}</span>
            <span className="cj-type-desc">{t.desc}</span>
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="cj-form" autoComplete="off">

        {/* ── Image ── */}
        <div className="cj-field">
          <label className="cj-label">Image {jobType === 'mcr' && mcrLoading && <span className="cj-hint">(loading…)</span>}</label>
          {jobType === 'mcr' ? (
            <select className="cj-select" value={image} onChange={e => setImage(e.target.value)}>
              <option value="">— select an MCR image —</option>
              {mcrImages.map(img => <option key={img} value={img}>{img}</option>)}
            </select>
          ) : (
            <input
              className="cj-input"
              value={image}
              onChange={e => setImage(e.target.value)}
              autoComplete="off"
              placeholder={
                jobType === 'dockerhub' ? 'e.g. python:3.11-slim' :
                jobType === 'ecr' ? 'e.g. 123456789.dkr.ecr.us-east-1.amazonaws.com/my-repo:latest' :
                'e.g. myregistry.azurecr.io/my-image:latest'
              }
            />
          )}
        </div>

        {/* ── Credentials (type-specific) ── */}
        {jobType === 'dockerhub' && (
          <div className="cj-optional-section">
            <label className="cj-toggle-label">
              <input type="checkbox" checked={useDockerCred} onChange={e => setUseDockerCred(e.target.checked)} />
              Credentials (private image)
            </label>
            {!useDockerCred && <span className="cj-enable-hint">Enable to provide Docker Hub credentials for private images.</span>}
            <div className={`cj-cred-grid ${!useDockerCred ? 'cj-disabled' : ''}`}>
              <input className="cj-input" placeholder="Docker Hub username" name="cj_docker_user" value={dockerUser} onChange={e => setDockerUser(e.target.value)} disabled={!useDockerCred} autoComplete="off" data-1p-ignore="true" />
              <input className="cj-input" type="password" placeholder="Docker Hub password" name="cj_docker_pass" value={dockerPass} onChange={e => setDockerPass(e.target.value)} disabled={!useDockerCred} autoComplete="new-password" data-1p-ignore="true" />
            </div>
          </div>
        )}

        {jobType === 'ecr' && (
          <div className="cj-section">
            <label className="cj-label">AWS Credentials <span className="cj-required">required</span></label>
            <div className="cj-cred-grid">
              <input className="cj-input" placeholder="AWS Access Key ID" name="cj_aws_key" value={awsKey} onChange={e => setAwsKey(e.target.value)} autoComplete="off" data-1p-ignore="true" />
              <input className="cj-input" type="password" placeholder="AWS Secret Access Key" name="cj_aws_secret" value={awsSecret} onChange={e => setAwsSecret(e.target.value)} autoComplete="new-password" data-1p-ignore="true" />
              <input className="cj-input" placeholder="Region (e.g. us-east-1)" name="cj_aws_region" value={awsRegion} onChange={e => setAwsRegion(e.target.value)} autoComplete="off" data-1p-ignore="true" />
            </div>
          </div>
        )}

        {jobType === 'acr' && (
          <div className="cj-section">
            <label className="cj-label">Azure Credentials <span className="cj-required">required</span></label>
            <div className="cj-cred-grid">
              <input className="cj-input" placeholder="Azure username" name="cj_azure_user" value={azureUser} onChange={e => setAzureUser(e.target.value)} autoComplete="off" data-1p-ignore="true" />
              <input className="cj-input" type="password" placeholder="Azure password" name="cj_azure_pass" value={azurePass} onChange={e => setAzurePass(e.target.value)} autoComplete="new-password" data-1p-ignore="true" />
            </div>
          </div>
        )}

        {/* ── Optional: Command Args ── */}
        <div className="cj-optional-section">
          <label className="cj-toggle-label">
            <input type="checkbox" checked={useCommandArgs} onChange={e => setUseCommandArgs(e.target.checked)} />
            Command Arguments
          </label>
          {!useCommandArgs && <span className="cj-enable-hint">Enable to pass arguments to the container (e.g. email → abc@gmail.com).</span>}
          {renderKVList(cmdArgs, useCommandArgs, updateCmd, addCmd, removeCmd, 'flag name', 'value')}
        </div>

        {/* ── Optional: Resources ── */}
        <div className="cj-optional-section">
          <label className="cj-toggle-label">
            <input type="checkbox" checked={useResources} onChange={e => setUseResources(e.target.checked)} />
            Resource Requests
          </label>
          {!useResources && <span className="cj-enable-hint">Enable to set CPU and memory limits for this job.</span>}
          <div className={`cj-res-grid ${!useResources ? 'cj-disabled' : ''}`}>
            <div className="cj-field-inline">
              <label className="cj-label-sm">CPU (vCPU)</label>
              <input className="cj-input cj-input-sm" type="number" step="0.1" min="0.1" value={cpu} onChange={e => setCpu(e.target.value)} disabled={!useResources} />
            </div>
            <div className="cj-field-inline">
              <label className="cj-label-sm">Memory (GB)</label>
              <input className="cj-input cj-input-sm" type="number" step="0.25" min="0.25" value={memory} onChange={e => setMemory(e.target.value)} disabled={!useResources} />
            </div>
          </div>
        </div>

        {/* ── Optional: GPU (Premium only) ── */}
        {gpuAvailable && (
          <div className="cj-optional-section">
            <label className="cj-toggle-label">
              <input type="checkbox" checked={useGpu} onChange={e => setUseGpu(e.target.checked)} />
              Use GPU
            </label>
            {!useGpu
              ? <span className="cj-enable-hint">Enable to run this job on a GPU node (1&times; NVIDIA T4).</span>
              : <span className="cj-enable-hint">
                  Billed at your Premium GPU rate per GPU-hour (15-minute minimum) on top of
                  CPU and memory usage. GPU jobs are capped at 3 vCPU / 12 GB and may take a
                  few minutes to start while a GPU node is provisioned.
                </span>}
          </div>
        )}

        {/* ── Optional: Env Variables ── */}
        <div className="cj-optional-section">
          <label className="cj-toggle-label">
            <input type="checkbox" checked={useEnv} onChange={e => setUseEnv(e.target.checked)} />
            Environment Variables
          </label>
          {!useEnv && <span className="cj-enable-hint">Enable to inject environment variables into the container.</span>}
          {renderKVList(envVars, useEnv, updateEnv, addEnv, removeEnv, 'NAME', 'value')}
        </div>

        {/* ── Optional: ConfigMap ── */}
        <div className="cj-optional-section">
          <label className="cj-toggle-label">
            <input
              type="checkbox"
              checked={useConfigMap}
              onChange={e => setUseConfigMap(e.target.checked)}
            />
            ConfigMap
            {isShared && (
              <span className="cj-dedicated-badge">stored as env file on shared</span>
            )}
          </label>
          {!useConfigMap && <span className="cj-enable-hint">Enable to load environment variables from an existing ConfigMap.</span>}
          <div className={`cj-configmap-row ${!useConfigMap ? 'cj-disabled' : ''}`}>
            <select
              className="cj-select"
              value={configMapName}
              onChange={e => setConfigMapName(e.target.value)}
              disabled={!useConfigMap}
            >
              <option value="">
                {configMapsLoading ? 'Loading…' : configMaps.length === 0 ? '— no ConfigMaps found —' : '— select a ConfigMap —'}
              </option>
              {configMaps.map(cm => <option key={cm} value={cm}>{cm}</option>)}
            </select>
          </div>
        </div>

        {/* ── Optional: Secret ── */}
        <div className="cj-optional-section">
          <label className="cj-toggle-label">
            <input
              type="checkbox"
              checked={useSecret}
              onChange={e => setUseSecret(e.target.checked)}
            />
            Secret
            <span className="cj-dedicated-badge">write-only</span>
          </label>
          {!useSecret && <span className="cj-enable-hint">Enable to inject API keys, passwords or tokens from a Secret.</span>}
          <div className={`cj-configmap-row ${!useSecret ? 'cj-disabled' : ''}`}>
            <select
              className="cj-select"
              value={secretName}
              onChange={e => setSecretName(e.target.value)}
              disabled={!useSecret}
            >
              <option value="">
                {secretsLoading ? 'Loading…' : secrets.length === 0 ? '— no Secrets found —' : '— select a Secret —'}
              </option>
              {secrets.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* ── Messages ── */}
        {errorMsg && <p className="cj-error">{errorMsg}</p>}
        {resultMsg && <p className="cj-success">{resultMsg}</p>}

        {/* ── Submit ── */}
        <button type="submit" className="cj-submit" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create Job'}
        </button>
      </form>
    </div>
  );
}
